import {type PropsWithChildren, useCallback, useEffect, useRef, useState} from "react";
import {useCombatants} from "@/encounter/combatants/CombatantHooks.ts";
import {useAreaEffect} from "@/encounter/effect/AreaEffectHooks.ts";
import {
    TableTopHistoryContext,
    type TransformHistoryEntry,
    type TransformHistoryTarget,
    type TransformSnapshot,
} from "@/tabletop/history/TableTopHistoryHooks.ts";

const MAX_HISTORY_ENTRIES = 100;

const isTextInput = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false;
    return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
};

const toTransformSnapshot = ({id, position, rotation, levelId}: TransformSnapshot): TransformSnapshot => ({
    id,
    position: {...position},
    rotation,
    levelId,
});

const transformSnapshotsEqual = (
    before: TransformSnapshot | TransformSnapshot[],
    after: TransformSnapshot | TransformSnapshot[],
) => {
    const beforeSnapshots = Array.isArray(before) ? before : [before];
    const afterSnapshots = Array.isArray(after) ? after : [after];
    if (beforeSnapshots.length !== afterSnapshots.length) return false;

    const afterById = new Map(afterSnapshots.map(snapshot => [snapshot.id, snapshot]));
    return beforeSnapshots.every(snapshot => {
        const other = afterById.get(snapshot.id);
        return other !== undefined
            && snapshot.position.x === other.position.x
            && snapshot.position.y === other.position.y
            && snapshot.rotation === other.rotation
            && snapshot.levelId === other.levelId;
    });
};

type ActiveHistoryInteraction =
    | {type: "combatants"; before: TransformSnapshot[]}
    | {type: "areaEffect"; before: TransformSnapshot};

export default function TableTopHistoryProvider({children}: PropsWithChildren) {
    const {combatants, update: updateCombatants} = useCombatants();
    const {effects, updateEffect} = useAreaEffect();
    const [undoStack, setUndoStack] = useState<TransformHistoryEntry[]>([]);
    const [redoStack, setRedoStack] = useState<TransformHistoryEntry[]>([]);
    const interactionActiveRef = useRef(false);
    const activeHistoryInteractionRef = useRef<ActiveHistoryInteraction | undefined>(undefined);

    const applyCombatantSnapshots = useCallback((snapshots: TransformSnapshot[]) => {
        const snapshotsById = new Map(snapshots.map(snapshot => [snapshot.id, snapshot]));
        const updated = combatants.flatMap(combatant => {
            const snapshot = snapshotsById.get(combatant.id);
            return snapshot ? [{
                ...combatant,
                position: {...snapshot.position},
                rotation: snapshot.rotation ?? combatant.rotation,
                levelId: snapshot.levelId ?? combatant.levelId,
            }] : [];
        });
        if (updated.length > 0) void updateCombatants(updated, "immediate");
    }, [combatants, updateCombatants]);

    const applyAreaEffectSnapshot = useCallback((snapshot: TransformSnapshot) => {
        const effect = effects.find(candidate => candidate.id === snapshot.id);
        if (!effect) return;
        updateEffect({
            ...effect,
            position: {...snapshot.position},
            rotation: snapshot.rotation,
        }, "immediate");
    }, [effects, updateEffect]);

    const applyHistoryEntry = useCallback((
        entry: TransformHistoryEntry,
        state: "before" | "after",
    ) => {
        if (entry.type === "combatants") {
            applyCombatantSnapshots(entry[state]);
        } else {
            applyAreaEffectSnapshot(entry[state]);
        }
    }, [applyAreaEffectSnapshot, applyCombatantSnapshots]);

    const record = useCallback((entry: TransformHistoryEntry) => {
        if (transformSnapshotsEqual(entry.before, entry.after)) return;
        setUndoStack(current => [...current, entry].slice(-MAX_HISTORY_ENTRIES));
        setRedoStack([]);
    }, []);

    const undo = useCallback(() => {
        const entry = undoStack[undoStack.length - 1];
        if (!entry) return;
        applyHistoryEntry(entry, "before");
        setUndoStack(current => current.slice(0, -1));
        setRedoStack(current => [...current, entry]);
    }, [applyHistoryEntry, undoStack]);

    const redo = useCallback(() => {
        const entry = redoStack[redoStack.length - 1];
        if (!entry) return;
        applyHistoryEntry(entry, "after");
        setRedoStack(current => current.slice(0, -1));
        setUndoStack(current => [...current, entry].slice(-MAX_HISTORY_ENTRIES));
    }, [applyHistoryEntry, redoStack]);

    const clearHistory = useCallback(() => {
        setUndoStack([]);
        setRedoStack([]);
    }, []);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (interactionActiveRef.current || isTextInput(event.target)) return;
            if (!(event.ctrlKey || event.metaKey)) return;

            if (event.key.toLowerCase() === "z") {
                const action = event.shiftKey ? redo : undo;
                if (event.shiftKey ? redoStack.length > 0 : undoStack.length > 0) {
                    event.preventDefault();
                    action();
                }
            } else if (event.key.toLowerCase() === "y" && redoStack.length > 0) {
                event.preventDefault();
                redo();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [redo, redoStack.length, undo, undoStack.length]);

    const beginInteraction = (target?: TransformHistoryTarget) => {
        interactionActiveRef.current = true;
        if (target?.type === "combatants") {
            const ids = new Set(target.ids);
            activeHistoryInteractionRef.current = {
                type: "combatants",
                before: combatants.filter(item => ids.has(item.id)).map(toTransformSnapshot),
            };
        } else if (target?.type === "areaEffect") {
            const effect = effects.find(item => item.id === target.id);
            activeHistoryInteractionRef.current = effect
                ? {type: "areaEffect", before: toTransformSnapshot(effect)}
                : undefined;
        } else {
            activeHistoryInteractionRef.current = undefined;
        }
    };
    const endInteraction = (after?: TransformSnapshot | TransformSnapshot[]) => {
        interactionActiveRef.current = false;
        const interaction = activeHistoryInteractionRef.current;
        activeHistoryInteractionRef.current = undefined;
        if (!interaction || !after) return;

        if (interaction.type === "combatants" && Array.isArray(after)) {
            record({
                type: "combatants",
                before: interaction.before,
                after: after.map(toTransformSnapshot),
            });
        } else if (interaction.type === "areaEffect" && !Array.isArray(after)) {
            record({
                type: "areaEffect",
                before: interaction.before,
                after: toTransformSnapshot(after),
            });
        }
    };
    const value = {
        canUndo: undoStack.length > 0,
        canRedo: redoStack.length > 0,
        beginInteraction,
        endInteraction,
        undo,
        redo,
        clearHistory,
    };

    return (
        <TableTopHistoryContext.Provider value={value}>
            {children}
        </TableTopHistoryContext.Provider>
    );
}
